// File: src/components/forms/PhoneField/PhoneField.tsx
// Purpose: Form input component

import React, { useState } from "react";
import { Phone } from "lucide-react";
import { TextField, TextFieldProps } from "../TextField";

/** 📌 Formats a phone number dynamically as you type */
const formatPhoneNumber = (value: string) => {
  const cleaned = value.replace(/\D/g, "").slice(0, 10); // ✅ Enforce max length 10
  let formatted = "";

  if (cleaned.length > 0) formatted = `(${cleaned.slice(0, 3)}`;
  if (cleaned.length > 3) formatted += `) ${cleaned.slice(3, 6)}`;
  if (cleaned.length > 6) formatted += `-${cleaned.slice(6, 10)}`;

  return formatted;
};

/** 📌 Validates phone number */
const validatePhoneNumber = (value: string) => {
  const cleaned = value.replace(/\D/g, ""); // Strip non-numeric chars
  if (cleaned.length === 0) return null; // ✅ No error when empty
  if (cleaned.length < 10) return "Phone number must be 10 digits"; // ⚠️ Error if less than 10 digits
  return null; // ✅ Valid phone number
};

export const PhoneField: React.FC<Omit<TextFieldProps, "type" | "maxLength">> = (props) => {
  const [rawValue, setRawValue] = useState<string>(""); // Stores raw numeric value
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false); // Tracks focus to delay validation

  return (
    <TextField
      {...props}
      type="tel"
      icon={Phone}
      placeholder="(123) 456-7890"
      value={formatPhoneNumber(rawValue)} // ✅ Always display formatted value
      maxLength={14} // ✅ Ensures formatting fits inside input field (formatted as (xxx) xxx-xxxx)
      onChange={(e) => {
        let cleaned = e.target.value.replace(/\D/g, ""); // ✅ Remove non-numeric characters

        if (cleaned.length <= 10) {
          setRawValue(cleaned); // ✅ Ensure no extra digits
        }
        
        if (touched) {
          setError(validatePhoneNumber(cleaned)); // ✅ Validate in real-time after blur
        }
      }}
      onBlur={() => {
        setTouched(true);
        setError(validatePhoneNumber(rawValue)); // ✅ Show error after blur
      }}
      error={touched ? error : undefined} // ✅ Show error only after blur
    />
  );
};